import { Component } from '@theme/component';

/**
 * Delivery estimate — turns a postcode into an estimated delivery window and a
 * cash-on-delivery answer, using rules the merchant configures in the theme
 * editor. Swapping these rules for a courier serviceability API later only
 * means replacing `estimateFor`.
 */

const STORAGE_KEY = 'altior:postcode';
const TICK_HOLD_MS = 1800;

/** @returns {string | null} */
function readPostcode() {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/** @param {string} postcode */
function writePostcode(postcode) {
  try {
    localStorage.setItem(STORAGE_KEY, postcode);
  } catch {
    /* Storage unavailable — the estimate still works for this page view. */
  }
}

/**
 * Splits a comma or space separated list of postcode prefixes.
 *
 * @param {string | undefined} source
 * @returns {string[]}
 */
function parsePrefixes(source) {
  return String(source ?? '')
    .split(/[\s,]+/)
    .map((prefix) => prefix.trim())
    .filter(Boolean);
}

/**
 * @param {string} postcode
 * @param {string[]} prefixes
 */
function matchesPrefix(postcode, prefixes) {
  return prefixes.some((prefix) => postcode.startsWith(prefix));
}

/**
 * Adds calendar days, then rolls off Sunday since couriers here rarely deliver
 * on it. Kept separate so the working-week rule is easy to change.
 *
 * @param {Date} from
 * @param {number} days
 */
function addDeliveryDays(from, days) {
  const date = new Date(from.getTime());
  date.setDate(date.getDate() + days);
  if (date.getDay() === 0) date.setDate(date.getDate() + 1);
  return date;
}

/** @param {Date} date */
function formatDate(date) {
  const locale = document.documentElement.lang || 'en';
  try {
    return new Intl.DateTimeFormat(locale, { weekday: 'short', day: 'numeric', month: 'short' }).format(date);
  } catch {
    return date.toDateString();
  }
}

/**
 * @typedef {object} EstimateConfig
 * @property {number} dispatchDays
 * @property {number} metroDays
 * @property {number} standardDays
 * @property {string[]} metroPrefixes
 * @property {boolean} codEnabled
 * @property {string[]} codExcludedPrefixes
 * @property {string[]} unservicedPrefixes
 * @property {Date | null} [shipsAt] - A future pre-order ship date; transit counts from it.
 */

/**
 * @param {string} postcode
 * @param {EstimateConfig} config
 * @returns {{serviced: boolean, from?: Date, to?: Date, cod?: boolean}}
 */
export function estimateFor(postcode, config) {
  if (matchesPrefix(postcode, config.unservicedPrefixes)) return { serviced: false };

  const isMetro = matchesPrefix(postcode, config.metroPrefixes);
  const transit = isMetro ? config.metroDays : config.standardDays;
  const now = new Date();

  // A pre-order leaves on its ship date, so quoting from today would promise
  // delivery weeks before anything is dispatched.
  const from =
    config.shipsAt && config.shipsAt > now
      ? addDeliveryDays(config.shipsAt, transit)
      : addDeliveryDays(now, config.dispatchDays + transit);

  return {
    serviced: true,
    from,
    // A one-day spread reads as the estimate it is, rather than a promise.
    // Counted from `from` so a Sunday roll-forward cannot collapse the range
    // onto a single day.
    to: addDeliveryDays(from, 1),
    cod: config.codEnabled && !matchesPrefix(postcode, config.codExcludedPrefixes),
  };
}

/**
 * @typedef {object} DeliveryEstimateRefs
 * @property {HTMLInputElement} [input]
 * @property {HTMLElement} [output]
 * @property {HTMLElement} [window]
 * @property {HTMLElement} [cod]
 * @property {HTMLElement} [tick]
 *
 * @extends Component<DeliveryEstimateRefs>
 */
export class DeliveryEstimate extends Component {
  #applying = false;

  /**
   * On a product page a variant change re-renders this section from markup the
   * server rendered blank. `updatedCallback` cannot carry this: the replacement
   * tree comes from DOMParser, so its elements are never upgraded and morph's
   * `instanceof Component` check does not match them.
   */
  #observer = new MutationObserver(() => this.#reapply());

  connectedCallback() {
    super.connectedCallback();
    this.#restore();
    this.#observer.observe(this, { childList: true, subtree: true, characterData: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer.disconnect();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#reapply();
  }

  /** Repaints the estimate after something rewrote our DOM. */
  #reapply() {
    if (this.#applying) return;
    this.#applying = true;

    this.#restore();

    // Discard the records our own writes just queued, so this cannot re-enter.
    this.#observer.takeRecords();
    this.#applying = false;
  }

  /** @returns {EstimateConfig} */
  get #config() {
    const { dataset } = this;
    return {
      dispatchDays: Number(dataset.dispatchDays ?? 1) || 0,
      metroDays: Number(dataset.metroDays ?? 2) || 0,
      standardDays: Number(dataset.standardDays ?? 4) || 0,
      metroPrefixes: parsePrefixes(dataset.metroPrefixes),
      codEnabled: dataset.codEnabled !== 'false',
      codExcludedPrefixes: parsePrefixes(dataset.codExcludedPrefixes),
      unservicedPrefixes: parsePrefixes(dataset.unservicedPrefixes),
      shipsAt: this.#preorderShipDate(),
    };
  }

  /**
   * Reads the ship date from the pre-order notice on the same page rather than
   * keeping a second copy here that could drift. The notice removes itself once
   * the date passes, which returns this to normal dispatch timing.
   * @returns {Date | null}
   */
  #preorderShipDate() {
    const scope = this.closest('.shopify-section') ?? document;
    const iso = scope.querySelector('.preorder time[datetime]')?.getAttribute('datetime');
    const time = iso ? Date.parse(iso) : NaN;
    return Number.isNaN(time) ? null : new Date(time);
  }

  #restore() {
    const saved = readPostcode();
    const input = this.refs.input;
    if (!saved || !(input instanceof HTMLInputElement)) return;

    if (!input.value) input.value = saved;
    this.check();
  }

  /** @param {Event} [event] */
  handleSubmit(event) {
    event?.preventDefault();
    this.check({ userInitiated: true });
  }

  /** Enter checks the postcode, matching the button next to the field. */
  handleKeydown(/** @type {KeyboardEvent} */ event) {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    this.check({ userInitiated: true });
  }

  /**
   * Validates the postcode and paints the estimate. Feedback animations only
   * play when the shopper asked, not when a saved postcode is restored.
   * @param {{userInitiated?: boolean}} [options]
   */
  check({ userInitiated = false } = {}) {
    const input = this.refs.input;
    const output = this.refs.output;
    if (!(input instanceof HTMLInputElement) || !(output instanceof HTMLElement)) return;

    const motion = userInitiated ? window.AltiorMotion : undefined;
    const postcode = input.value.replace(/\D/g, '');

    // The pattern is merchant-editable, so a bad one must not break the block.
    let pattern;
    try {
      pattern = new RegExp(this.dataset.pattern || '^[1-9][0-9]{5}$');
    } catch {
      pattern = /^[1-9][0-9]{5}$/;
    }

    if (!pattern.test(postcode)) {
      this.#paint('invalid', this.dataset.invalidText ?? '');
      motion?.shake(input.closest('.delivery-estimate__field'));
      return;
    }

    writePostcode(postcode);

    const result = estimateFor(postcode, this.#config);

    if (!result.serviced || !result.from || !result.to) {
      this.#paint('unserviced', this.dataset.unservicedText ?? '');
      motion?.revealChange(output);
      return;
    }

    const template = this.dataset.estimateText ?? 'Delivery by [range]';
    const range = `${formatDate(result.from)} – ${formatDate(result.to)}`;

    this.#paint('serviced', template.replace('[range]', range));

    const cod = this.refs.cod;
    if (cod instanceof HTMLElement) {
      cod.textContent = result.cod ? (this.dataset.codText ?? '') : (this.dataset.noCodText ?? '');
      cod.classList.add('delivery-estimate__cod--visible');
    }

    motion?.playTick(this.refs.tick, { hold: TICK_HOLD_MS });
    motion?.revealChange(output);
  }

  /**
   * @param {'invalid' | 'unserviced' | 'serviced'} state
   * @param {string} message
   */
  #paint(state, message) {
    const output = this.refs.output;
    if (!(output instanceof HTMLElement)) return;

    output.textContent = message;
    output.dataset.state = state;
    output.classList.add('delivery-estimate__output--visible');

    if (state !== 'serviced') {
      const cod = this.refs.cod;
      if (cod instanceof HTMLElement) cod.classList.remove('delivery-estimate__cod--visible');
    }
  }
}

if (!customElements.get('delivery-estimate-component')) {
  customElements.define('delivery-estimate-component', DeliveryEstimate);
}
